import axios from "axios";
import Cookies from "js-cookie";
import { getDateRange } from "../utils/dateUtils";
import dayjs from "dayjs";
const API_URL = process.env.REACT_APP_API_URL;
const API_TIMEOUT = 60000;

const getHeaders = () => {
  return {
    Authorization: `Bearer ${Cookies.get("__auth")}`,
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const getHeadersWithoutAuthorization = () => {
  return {
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const fetchDevices = async () => {
  try {
    const response = await axios.get(`${API_URL}/devices/linked`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    if (response.data && response.data.data) {
      return response.data.data.map((item) => ({
        deviceId: item.device.id,
        chargePointId: item.device.deviceId,
        buildingId: item.device.buildingId,
        buildingName: item.device.buildingName,
        country: item.device.country,
        currency: item.currency,
        tariffValue: item.tariff,
        id: item.id,
        createdAt: item.createdAt
          ? new Date(item.createdAt).toISOString()
          : null,
      }));
    } else if (response.status === 204) {
      return [];
    }
  } catch (error) {
    throw error;
  }
};

const pairDevice = async (deviceData) => {
  const sanitizedData = {
    ...deviceData,
    chargePointId: deviceData.chargePointId.replace(/\s+/g, " ").trim(),
  };
  try {
    const response = await axios.post(
      `${API_URL}/user-device-pairings`,
      { deviceId: sanitizedData.chargePointId },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const deleteDevice = async (id) => {
  try {
    const response = await axios.delete(
      `${API_URL}/user-device-pairings/${id}`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const fetchConsumptionData = async (
  selectedChargingPoint,
  view,
  selectedDate
) => {
  const { startDate, endDate } = getDateRange(view, selectedDate);
  const url = `${API_URL}/reports/charge-points/${selectedChargingPoint}/energy?timeframe=${view}&startDate=${startDate.format(
    "YYYY-MM-DD"
  )}&endDate=${endDate.format("YYYY-MM-DD")}`;
  try {
    const response = await axios.get(url, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching consumption data:", error);
    throw error;
  }
};

const signUp = async (formData) => {
  try {
    const { email, firstName, lastName, password, chargePointId } = formData;
    const fullName = `${firstName} ${lastName}`;
    let url = `${API_URL}/auth/sign-up`;
    if (chargePointId) {
      url = url + `?chargePointId=${chargePointId}`;
    }
    const response = await axios.post(
      url,
      {
        email,
        fullName,
        password,
      },
      {
        headers: getHeadersWithoutAuthorization(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message, status: 500 };
  }
};

const changePassword = async ({
  hasOldPassword,
  oldPassword,
  password,
  confirmPassword,
}) => {
  const url = `${API_URL}/auth/change-password`;

  try {
    const response = await axios.put(
      url,
      {
        oldPassword: hasOldPassword ? oldPassword : undefined,
        newPassword: password,
        confirmPassword,
      },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const checkRegistrationStatus = async () => {
  try {
    const response = await axios.get(`${API_URL}/auth/registration-status`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.status === "0108";
  } catch (error) {
    console.error("Failed to check registration status", error);
    throw error;
  }
};

const forgotPassword = async (email) => {
  const url = `${API_URL}/auth/forgot-password/${email}`;
  try {
    const response = await axios.get(url, {
      headers: getHeadersWithoutAuthorization(),
      timeout: API_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const fetchUsernameAndEmail = async () => {
  const url = `${API_URL}/users/me`;
  try {
    const response = await axios.get(url, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

const validateResetPasswordLink = async (token, key) => {
  try {
    const response = await axios.post(
      `${API_URL}/auth/validate-link`,
      {
        token,
        key,
      },
      {
        headers: getHeadersWithoutAuthorization(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data.success;
  } catch (error) {
    throw error;
  }
};

const resetPassword = async (password, token, key) => {
  try {
    const response = await axios.post(
      `${API_URL}/auth/reset-password`,
      {
        password,
        token,
        key,
      },
      {
        headers: getHeadersWithoutAuthorization(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const validateLinkAndSignIn = async (token, key, signIn, navigate, chargePointId) => {
  try {
    const validateResponse = await axios.post(
      `${API_URL}/auth/validate-link`,
      {
        token,
        key,
      },
      {
        headers: getHeadersWithoutAuthorization(),
        timeout: API_TIMEOUT,
      }
    );
    if (!validateResponse.data.success) {
      console.error("Link validation failed:", validateResponse.data.message);
      return;
    }

    const verifyResponse = await axios.post(
      `${API_URL}/auth/verify`,
      {
        token,
        key,
      },
      {
        headers: getHeadersWithoutAuthorization(),
        timeout: API_TIMEOUT,
      }
    );
    if (verifyResponse.data.success) {
      const token = verifyResponse.data.data;
      if (
        signIn({
          auth: {
            token,
            type: "Bearer",
            expiresIn: 7 * 24 * 60 * 60,
          },
        })
      ) {
        if (chargePointId) {
          navigate(`/charge-points/pair-device/${chargePointId}`);
        } else {
          navigate("/");
        }
      } else {
        console.error("Sign-in failed");
      }
    }
  } catch (error) {
    console.error("Error validating link:", error);
  }
};

const login = async (
  formData,
  navigate,
  signIn,
  setError,
  setOpenSnackbar,
  setStatusCode
) => {
  try {
    const response = await axios.post(`${API_URL}/auth/sign-in`, formData, {
      headers: getHeadersWithoutAuthorization(),
      timeout: API_TIMEOUT,
    });
    if (response.data.status === "0103") {
      navigate("/check-email", {
        state: {
          email: formData.email,
          text: "Your account is currently not verified. Please check your email to complete the verification process.",
        },
      });
    } else if (response.data.success && response.status === 200) {
      const token = response.data.data;
      if (
        signIn({
          auth: {
            token,
            type: "Bearer",
            expiresIn: 7 * 24 * 60 * 60,
          },
          userState: {
            email: formData.email,
          },
        })
      ) {
        const storedUrl = localStorage.getItem("currentUrl");
        if (storedUrl) {
          navigate(storedUrl);
        } else {
          navigate("/");
        }
        localStorage.removeItem("currentUrl");
      } else {
        setError("Sign-in failed");
        setOpenSnackbar(true);
      }
    } else {
      setError(response.data.message);
      setOpenSnackbar(true);
    }
  } catch (error) {
    if (error.response && error.response.data) {
      setError(error.response.data.message || "An unknown error occurred");
      setStatusCode(
        error.response.data.statusCode || error.response.data.status || 500
      );
    } else {
      setError("An unknown error occurred");
      setStatusCode(500);
    }
    setOpenSnackbar(true);
  }
};

const fetchChargingPointData = async (chargePointId) => {
  try {
    const response = await axios.get(
      `${API_URL}/reports/charge-point/${chargePointId}/power`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching device data:", error);
    throw error;
  }
};

const getChargePointSwitchStatus = async (chargePointId) => {
  const requestTimestamp = Date.now();
  try {
    const response = await axios.get(
      `${API_URL}/reports/charge-point/${chargePointId}/switch`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
        params: { requestTimestamp },
        data: { requestTimestamp },
      }
    );
    if (response.data && response.data.success) {
      return {
        status: response.status,
        message: response.data.message,
        switchStatus: response.data.data.switchStatus,
        celsiusTemperature: response.data.data.celsiusTemperature,
        fahrenheitTemperature: response.data.data.fahrenheitTemperature,
        requestTimestamp: response.data.data.requestTimestamp,
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch switch status");
    }
  } catch (error) {
    error.requestTimestamp = requestTimestamp;
    console.error("Error fetching switch status:", error);
    throw error;
  }
};

const sendFcmToken = async (fcmToken) => {
  try {
    const response = await axios.put(
      `${API_URL}/auth/fcmToken/update`,
      {
        fcmToken,
      },
      {
        headers: {
          Authorization: `Bearer ${Cookies.get("__auth")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending FCM token:", error);
    throw error;
  }
};
const getLatestFiveNotifications = async () => {
  try {
    const response = await axios.get(`${API_URL}/notifications/latest/4`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch notifications:", error);
    throw error;
  }
};

const markNotificationsAsRead = async (notifications) => {
  try {
    const response = await axios.put(
      `${API_URL}/notifications/mark-as-read`,
      notifications,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to mark notifications as read:", error);
    throw error;
  }
};

const switchChargePoint = async (chargePointId, building, action) => {
  const requestTimestamp = Date.now();
  try {
    const response = await axios.post(
      `${API_URL}/mqtt/charge-points/${chargePointId}/switch`,
      { building, action, requestTimestamp },
      {
        headers: {
          Authorization: `Bearer ${Cookies.get("__auth")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    error.requestTimestamp = requestTimestamp;
    console.error("Error switching device:", error);
    throw error;
  }
};

const fetchChargingSessionData = async (chargePointId) => {
  try {
    const response = await axios.get(
      `${API_URL}/devices/charge-points/${chargePointId}/charging-session`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    if (response.data && response.data.data) {
      return response.data;
    } else if (response.status === 204) {
      return [];
    }
  } catch (error) {
    console.error(
      `Failed to fetch charging session data for ${chargePointId}:`,
      error
    );
    throw error;
  }
};

const fetchChargingSessions = async (chargePointId, startDate, endDate) => {
  try {
    const response = await axios.get(
      `${API_URL}/devices/charge-points/${chargePointId}/charging-session`,
      {
        params: {
          startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
          endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
        },
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    if (response.data && response.data.data) {
      return response.data.data;
    } else if (response.status === 204) {
      return [];
    }
  } catch (error) {
    console.error("Error fetching charging sessions:", error);
    throw error;
  }
};

const fetchTariffDataByBuildingId = async (buildingId) => {
  try {
    const response = await axios.get(
      `${API_URL}/tariffs/building/${buildingId}`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching tariff data:", error);
    throw error;
  }
};

const addCardDetails = async (cardDetails) => {
  try {
    const response = await axios.post(`${API_URL}/cards`, cardDetails, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const fetchCardDetails = async () => {
  const url = `${API_URL}/cards`;
  try {
    const response = await axios.get(url, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

const deleteCard = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/cards/${id}`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const AddCardToken = async (tokenId) => {
  const url = `${API_URL}/cards`;
  try {
    const response = await axios.post(
      url,
      { tokenId },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const fetchUserBalance = async () => {
  const url = `${API_URL}/users/balance`;
  try {
    const response = await axios.get(url, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

const fetchPaymentHistory = async () => {
  try {
    const response = await axios.get(`${API_URL}/transactions/user`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    if (response.data && response.data.data) {
      return response.data.data.map((item) => ({
        id: item.id,
        amount: item.amount,
        timestamp: dayjs(item.timestamp).format("DD/MM/YYYY HH:mm"),
        paymentSuccess: item.paymentSuccess,
      }));
    } else if (response.status === 204) {
      return [];
    }
  } catch (error) {
    console.error("Error fetching charging sessions:", error);
    throw error;
  }
};

const topUpBalance = async () => {
  try {
    const response = await axios.post(
      `${API_URL}/cards/topup`,
      {},
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const createChargingSchedule = async (
  fromTime,
  toTime,
  selectedChargingPoint
) => {
  try {
    const response = await axios.post(
      `${API_URL}/charging-schedules`,
      {
        startTime: fromTime,
        endTime: toTime,
        deviceId: selectedChargingPoint,
      },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );

    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const fetchChargingSchedule = async (selectedChargingPoint) => {
  try {
    const response = await axios.get(
      `${API_URL}/charging-schedules/charge-points/${selectedChargingPoint}`,

      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );

    return response.data.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const updateChargingSchedule = async (fromTime, toTime, chargingScheduleId) => {
  try {
    const response = await axios.put(
      `${API_URL}/charging-schedules/${chargingScheduleId}`,
      {
        startTime: fromTime,
        endTime: toTime,
      },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );

    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const updateScheduleStatus = async (status, chargingScheduleId) => {
  try {
    const response = await axios.put(
      `${API_URL}/charging-schedules/${chargingScheduleId}/status`,
      {
        status,
      },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );

    return response.data;
  } catch (error) {
    throw (
      error.response?.data || {
        message: "An unknown error occurred",
        status: 500,
      }
    );
  }
};

const fetchSubscriptionByBuildingId = async (buildingId) => {
  try {
    const response = await axios.get(
      `${API_URL}/user-subscriptions/building/${buildingId}`,
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching subscription:", error);
    throw error;
  }
};

const fetchUserSubscriptions = async () => {
  try {
    const response = await axios.get(`${API_URL}/user-subscriptions`, {
      headers: getHeaders(),
      timeout: API_TIMEOUT,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching subscriptions:", error);
    throw error;
  }
};

const activateUserSubscription = async (buildingId) => {
  try {
    const response = await axios.post(
      `${API_URL}/user-subscriptions`,
      { buildingId },
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

const cancelUserSubscription = async (subscriptionId) => {
  try {
    const response = await axios.put(
      `${API_URL}/user-subscriptions/${subscriptionId}/cancel`,
      {},
      {
        headers: getHeaders(),
        timeout: API_TIMEOUT,
      }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export {
  fetchTariffDataByBuildingId,
  fetchDevices,
  pairDevice,
  deleteDevice,
  fetchConsumptionData,
  signUp,
  changePassword,
  checkRegistrationStatus,
  forgotPassword,
  fetchUsernameAndEmail,
  validateResetPasswordLink,
  resetPassword,
  validateLinkAndSignIn,
  login,
  fetchChargingPointData,
  getChargePointSwitchStatus,
  sendFcmToken,
  getLatestFiveNotifications,
  markNotificationsAsRead,
  switchChargePoint,
  fetchChargingSessionData,
  fetchChargingSessions,
  addCardDetails,
  fetchCardDetails,
  fetchPaymentHistory,
  deleteCard,
  AddCardToken,
  fetchUserBalance,
  topUpBalance,
  createChargingSchedule,
  fetchChargingSchedule,
  updateChargingSchedule,
  updateScheduleStatus,
  fetchSubscriptionByBuildingId,
  fetchUserSubscriptions,
  activateUserSubscription,
  cancelUserSubscription,
};
